import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, FileText, Image as ImageIcon, Save, Loader2, Eye, Trash2,
  ChevronDown, ChevronRight, Plus, RefreshCw, ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import RichTextEditor from '../components/RichTextEditor';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

import { BACKEND_URL } from '../config/api';

export default function AdminCMS() {
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [pageContent, setPageContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [images, setImages] = useState([]);
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    fetchPages();
    fetchImages();
  }, []);

  useEffect(() => {
    if (selectedPage) {
      fetchPageContent(selectedPage.page_id);
    }
  }, [selectedPage]);

  const fetchPages = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/pages`, {
        withCredentials: true
      });
      setPages(response.data);
      if (response.data.length > 0 && !selectedPage) {
        setSelectedPage(response.data[0]);
      }
    } catch (error) {
      toast.error('Failed to fetch pages');
    } finally {
      setLoading(false);
    }
  };

  const fetchPageContent = async (pageId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/pages/${pageId}`, {
        withCredentials: true
      });
      setPageContent(response.data.sections || {});
      // Expand all sections by default
      const expanded = {};
      (selectedPage?.sections || []).forEach(s => {
        expanded[s.section_id] = true;
      });
      setExpandedSections(expanded);
    } catch (error) {
      console.error('Failed to fetch page content');
      setPageContent({});
    }
  };

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/images`, {
        withCredentials: true
      });
      setImages(response.data);
    } catch (error) {
      console.error('Failed to fetch images');
    }
  };

  const handleSave = async () => {
    if (!selectedPage) return;

    setSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/cms/pages/${selectedPage.page_id}`,
        { sections: pageContent },
        { withCredentials: true }
      );
      toast.success('Content saved successfully!');
    } catch (error) {
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const handleSectionContentChange = (sectionId, content) => {
    setPageContent(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        content
      }
    }));
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/cms/images/${imageId}`, {
        withCredentials: true
      });
      toast.success('Image deleted');
      fetchImages();
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const getPreviewUrl = () => {
    if (!selectedPage) return '#';
    const routes = {
      landing: '/',
      terms: '/terms',
      privacy: '/privacy',
      about: '/about'
    };
    return routes[selectedPage.page_id] || '/';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5 text-slate-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Content Management</h1>
              <p className="text-sm text-slate-500">Edit page content and images</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowImageGallery(true)}
              className="btn-secondary flex items-center gap-2"
              data-testid="image-gallery-btn"
            >
              <ImageIcon className="h-4 w-4" />
              Image Gallery
            </button>
            <a
              href={getPreviewUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary flex items-center gap-2"
            >
              <Eye className="h-4 w-4" />
              Preview
            </a>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary flex items-center gap-2"
              data-testid="save-content-btn"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save Changes
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Sidebar - Page List */}
          <div className="col-span-3">
            <div className="bg-white rounded-xl border border-slate-200 p-4 sticky top-24">
              <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <FileText className="h-4 w-4 text-orange-500" />
                Pages
              </h2>
              <div className="space-y-1">
                {pages.map((page) => (
                  <button
                    key={page.page_id}
                    onClick={() => setSelectedPage(page)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      selectedPage?.page_id === page.page_id
                        ? 'bg-orange-100 text-orange-700 font-medium'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                    data-testid={`page-${page.page_id}`}
                  >
                    {page.title}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content - Editor */}
          <div className="col-span-9">
            {selectedPage ? (
              <div className="space-y-6">
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h2 className="text-lg font-bold text-slate-900 mb-2">
                    {selectedPage.title}
                  </h2>
                  <p className="text-sm text-slate-500 mb-6">
                    {selectedPage.description}
                  </p>

                  {/* Sections */}
                  <div className="space-y-4">
                    {selectedPage.sections?.map((section) => (
                      <div
                        key={section.section_id}
                        className="border border-slate-200 rounded-lg overflow-hidden"
                        data-testid={`section-${section.section_id}`}
                      >
                        <button
                          onClick={() => toggleSection(section.section_id)}
                          className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
                        >
                          <span className="font-medium text-slate-700">
                            {section.title}
                          </span>
                          {expandedSections[section.section_id] ? (
                            <ChevronDown className="h-4 w-4 text-slate-400" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-slate-400" />
                          )}
                        </button>
                        
                        {expandedSections[section.section_id] && (
                          <div className="p-4">
                            <RichTextEditor
                              content={pageContent[section.section_id]?.content || ''}
                              onChange={(content) => handleSectionContentChange(section.section_id, content)}
                              placeholder={`Enter content for ${section.title}...`}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Quick Tips */}
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                  <h3 className="font-medium text-orange-800 mb-2">Tips</h3>
                  <ul className="text-sm text-orange-700 space-y-1">
                    <li>• Use the toolbar to format text, add headings, lists, and more</li>
                    <li>• Click the image icon to upload and insert images</li>
                    <li>• Changes are saved when you click "Save Changes"</li>
                    <li>• Use Preview to see how the page looks to visitors</li>
                  </ul>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Select a page to edit</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Gallery Modal */}
      <Dialog open={showImageGallery} onOpenChange={setShowImageGallery}>
        <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5 text-orange-500" />
              Image Gallery
            </DialogTitle>
          </DialogHeader>

          <div className="mt-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-slate-500">
                {images.length} images uploaded
              </p>
              <button
                onClick={fetchImages}
                className="btn-secondary text-sm flex items-center gap-1"
              >
                <RefreshCw className="h-3 w-3" />
                Refresh
              </button>
            </div>

            {images.length > 0 ? (
              <div className="grid grid-cols-3 gap-4">
                {images.map((image) => (
                  <div
                    key={image.image_id}
                    className="relative group rounded-lg overflow-hidden border border-slate-200"
                  >
                    <img
                      src={`${BACKEND_URL}${image.url}`}
                      alt={image.original_name}
                      className="w-full h-32 object-cover"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`${BACKEND_URL}${image.url}`);
                          toast.success('URL copied!');
                        }}
                        className="p-2 bg-white rounded-lg hover:bg-slate-100"
                        title="Copy URL"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteImage(image.image_id)}
                        className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="p-2 text-xs text-slate-600 truncate">
                      {image.original_name}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <ImageIcon className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p>No images uploaded yet</p>
                <p className="text-sm mt-1">Upload images using the editor</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
